import {
  Button,
  Card,
  CardContent,
  CardHeader,
  H4,
  H5,
  H6,
} from "@sellar-io/components";
import { pluralize } from "@sellar-io/common";
import { useState } from "react";
import { twMerge } from "tailwind-merge";
import { SpinnerLegacy } from "../../../components";
import { InputContainer, InputText } from "@sellar-io/components";
import {
  Dialog,
  DialogNavigation,
  DialogContent,
  DialogCTA,
} from "../../../components/modal/Dialog";
import { Switch } from "@sellar-io/components";
import type { FeatureFlagAdminListResponseType } from "@sellar-io/api-v2/types";
import { useSellarCurrentBusiness } from "../../../hooks/useSellarCurrentBusiness.shared";
import { useAdminFeatureFlagGloballyActiveUpdate } from "../../../hooks/api/v2/featureFlag/useAdminFeatureFlagGloballyActiveUpdate";
import { useAdminFeatureFlagBusinessEnable } from "../../../hooks/api/v2/featureFlag/useAdminFeatureFlagBusinessEnable";
import { useAdminFeatureFlagBusinessDisable } from "../../../hooks/api/v2/featureFlag/useAdminFeatureFlagBusinessDisable";
import { useFeatureFlagAdminList } from "../../../hooks/api/v2/featureFlag/useFeatureFlagAdminList";

export const RemoteFeatureFlagCard = () => {
  const { business } = useSellarCurrentBusiness();
  const [category, setCategory] = useState("all");
  const [status, setStatus] = useState("all");

  if (!business) throw new Error();

  const query = useFeatureFlagAdminList();

  if (query.isLoading) return <SpinnerLegacy />;

  const featureFlags = query.data ?? [];
  const categories = [
    ...new Set(featureFlags.map((flag) => flag.category)),
  ].sort();
  const statuses = [...new Set(featureFlags.map((flag) => flag.status))].sort();
  const filteredFeatureFlags = featureFlags.filter(
    (flag) =>
      (category === "all" || flag.category === category) &&
      (status === "all" || flag.status === status),
  );

  return (
    <Card className={twMerge("bg-white", "m-auto w-full max-w-[900px]")}>
      <CardHeader className={twMerge("bg-white", "p-4")}>
        <H4>Remote Feature Flags</H4>
        <H6>
          These flags can be set either on a per business level, or globally.
          They are not set on a per user basis.
        </H6>
      </CardHeader>
      <div className="grid gap-2 border-y border-zinc-200 p-4 sm:grid-cols-2">
        <select
          className="rounded border border-zinc-300 bg-white p-2"
          value={category}
          onChange={(event) => setCategory(event.target.value)}
        >
          <option value="all">All categories</option>
          {categories.map((value) => (
            <option key={value} value={value}>
              {value}
            </option>
          ))}
        </select>
        <select
          className="rounded border border-zinc-300 bg-white p-2"
          value={status}
          onChange={(event) => setStatus(event.target.value)}
        >
          <option value="all">All statuses</option>
          {statuses.map((value) => (
            <option key={value} value={value}>
              {value}
            </option>
          ))}
        </select>
      </div>
      <CardContent className={"flex flex-col p-0"}>
        {filteredFeatureFlags.map((featureFlag) => {
          return (
            <RemoteFeatureFlag key={featureFlag.id} featureFlag={featureFlag} />
          );
        })}
      </CardContent>
    </Card>
  );
};

const RemoteFeatureFlag = ({
  featureFlag,
}: {
  featureFlag: FeatureFlagAdminListResponseType[number];
}) => {
  const { business } = useSellarCurrentBusiness();
  const createFlag = useAdminFeatureFlagBusinessEnable(featureFlag.id);
  const deleteFlag = useAdminFeatureFlagBusinessDisable(featureFlag.id);

  const [active, setActive] = useState(false);

  const getGlobalActiveString = (globallyActive: boolean | null) => {
    if (globallyActive === null) return "Use defaults";
    if (globallyActive) return "On";
    else return "Off";
  };

  const getFeatureFlagActiveStatus = (
    featureFlag: FeatureFlagAdminListResponseType[number],
  ) => {
    if (featureFlag.globallyActive === true) return "Enabled globally";
    if (featureFlag.globallyActive === false) return "Disabled globally";
    return `Enabled on ${featureFlag.businessCount} ${pluralize("business", featureFlag.businessCount)}`;
  };

  return (
    <>
      <div
        key={featureFlag.id}
        className={twMerge(
          "flex items-center justify-between justify-items-center gap-4 p-4",
          typeof featureFlag.globallyActive === "boolean" &&
            (featureFlag.globallyActive ? "bg-green-100" : "bg-red-100"),
        )}
      >
        <div>
          <H5>{featureFlag.name}</H5>
          <H6>{featureFlag.description}</H6>
          <div className="flex flex-wrap gap-2 py-1 text-xs font-semibold text-zinc-600">
            <span className="rounded-full bg-zinc-100 px-2 py-1">
              {featureFlag.category}
            </span>
            <span className="rounded-full bg-zinc-100 px-2 py-1">
              {featureFlag.status}
            </span>
            {featureFlag.author && (
              <span className="rounded-full bg-blue-100 px-2 py-1 text-blue-700">
                Owner: {featureFlag.author}
              </span>
            )}
          </div>
          <p className={"text-sm"}>
            <b>Status:</b> {getFeatureFlagActiveStatus(featureFlag)}
          </p>
        </div>
        <div className={"grid items-center gap-2 sm:grid-cols-[1fr_80px]"}>
          <b>Business:</b>
          <Switch
            disabled={
              createFlag.isPending ||
              deleteFlag.isPending ||
              typeof featureFlag.globallyActive === "boolean"
            }
            onClick={() => {
              if (!business) throw new Error("Cannot find business");
              if (featureFlag.active) {
                return deleteFlag.mutate();
              }
              return createFlag.mutate();
            }}
            checked={featureFlag.active}
          />
          <span>
            <b>Global:</b> {getGlobalActiveString(featureFlag.globallyActive)}
          </span>
          <Button size={"small"} onClick={() => setActive(true)}>
            Change
          </Button>
        </div>
      </div>
      <GlobalActivationFeatureFlagDialog
        featureFlag={featureFlag}
        active={active}
        onDismiss={() => setActive(false)}
      />
    </>
  );
};

const GlobalActivationFeatureFlagDialog = ({
  featureFlag,
  active,
  onDismiss: initialOnDismiss,
}: {
  featureFlag: FeatureFlagAdminListResponseType[number];
  active: boolean;
  onDismiss: () => void;
}) => {
  const [value, setValue] = useState("");

  const updateGloballyActiveMutation = useAdminFeatureFlagGloballyActiveUpdate(
    featureFlag.id,
  );

  const onDismiss = () => {
    setValue("");
    initialOnDismiss();
  };

  const isValidValue = value.toLowerCase() === featureFlag.name.toLowerCase();

  const updateGloballyActive = (globallyActive: boolean | null) =>
    updateGloballyActiveMutation.mutate(globallyActive, {
      onSuccess: () => onDismiss(),
    });

  return (
    <Dialog active={active} onDismiss={onDismiss}>
      <DialogNavigation
        title={`Change global activation for \`${featureFlag.name}\``}
        description={`This feature flag is enabled on the business level for ${featureFlag.businessCount} ${pluralize("business", featureFlag.businessCount)}`}
        leftIcon={"CHEVRON_DOWN"}
        onClickLeftIcon={onDismiss}
      />
      <DialogContent className={"flex flex-col gap-4"}>
        <ul>
          <li>
            <b>Enable</b> will turn <b>ON</b> the feature flag for <b>ALL</b>{" "}
            businesses.
          </li>
          <li>
            <b>Disable</b> will turn <b>OFF</b> the feature flag for <b>ALL</b>{" "}
            businesses.
          </li>
          <li>
            <b>Use defaults</b> will turn <b>ON</b> the feature flag for{" "}
            <b>
              {featureFlag.businessCount}{" "}
              {pluralize("business", featureFlag.businessCount)}
            </b>{" "}
            but <b>OFF</b> for all other businesses.
          </li>
        </ul>
        Please enter the name of the feature flag to confirm{" "}
        <InputContainer>
          <InputText
            value={value}
            onChange={(e) => setValue(e.target.value)}
          ></InputText>
        </InputContainer>
      </DialogContent>
      <DialogCTA>
        <Button
          color={"green"}
          disabled={!isValidValue}
          loading={updateGloballyActiveMutation.isPending}
          onClick={() => updateGloballyActive(true)}
        >
          Enable
        </Button>
        <Button
          disabled={!isValidValue}
          loading={updateGloballyActiveMutation.isPending}
          onClick={() => updateGloballyActive(null)}
        >
          Use defaults
        </Button>
        <Button
          color={"red"}
          disabled={!isValidValue}
          loading={updateGloballyActiveMutation.isPending}
          onClick={() => updateGloballyActive(false)}
        >
          Disable
        </Button>
      </DialogCTA>
    </Dialog>
  );
};
